import { useEffect, useState, useRef } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Box } from '@mui/material'
import { signInWithCustomToken } from 'firebase/auth'

import Loading from '@hapana/shared/web/components/shared/loading/loading.component'
import { useToast } from '@hapana/shared/web/components/shared/toast/toast.hooks'
import { TOAST_PROPS_OPTIONS } from '@hapana/shared/web/components/shared/toast/toast.constants'
import { getErrorMessage } from '@hapana/shared/web/services/gqty'
import { auth, setAuthTenant } from '../../config/firebase.config'
import { getStaffTenantId, fetchTenantConfig } from '../../services/tenant.service'
import useSiteStore from '../../stores/site.store'

import { styles } from './login-as.styles'
import { ROUTES } from '../../app.constants'

const LoginAs = () => {
  const { t } = useTranslation('core')
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(true)
  const { openToast } = useToast()
  const { setCurrentSite, clearCurrentSite } = useSiteStore()
  const hasStarted = useRef(false)

  useEffect(() => {
    if (hasStarted.current) {
      return
    }
    hasStarted.current = true

    const handleLoginAs = async () => {
      try {
        const sessionId = searchParams.get('sessionId')

        if (!sessionId) {
          navigate(ROUTES.login.path, { replace: true })
          return
        }
        const hostname = window.location.hostname
        const tenant = hostname.split('.')[0]

        const gatewayUrl = import.meta.env.VITE_GATEWAY_URL || 'http://localhost:3002'
        const response = await fetch(`${gatewayUrl}/api/auth/impersonation/validate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-tenant': tenant,
          },
          body: JSON.stringify({ sessionId }),
        })

        const validationResult = await response.json()

        if (!response.ok || !validationResult.valid || !validationResult.customToken) {
          throw new Error(validationResult.error || 'Invalid session')
        }

        // Ensure the Firebase auth instance has the staff tenant set before signing in.
        // The custom token was created for the staff tenant on the server — the client
        // auth must match, otherwise the resulting ID token won't be tenant-scoped.
        let staffTenantId = getStaffTenantId()
        if (!staffTenantId) {
          const config = await fetchTenantConfig(tenant)
          staffTenantId = config?.tenants?.staff
        }
        if (staffTenantId) {
          setAuthTenant(staffTenantId)
        }

        await signInWithCustomToken(auth, validationResult.customToken)

        const sessionData = {
          sessionId,
          staffUser: validationResult.staffUser,
          adminUser: validationResult.adminUser,
          startedAt: new Date().toISOString(),
        }
        localStorage.setItem('activeImpersonationSession', JSON.stringify(sessionData))

        // Set site context if a specific location was targeted, otherwise clear to brand level
        if (validationResult.siteId) {
          setCurrentSite({ siteId: validationResult.siteId })
        } else {
          clearCurrentSite()
        }

        window.dispatchEvent(
          new CustomEvent('impersonationStarted', {
            detail: { sessionId },
          })
        )

        await new Promise((resolve) => setTimeout(resolve, 200))

        // Navigate to site-level dashboard if a specific location was targeted
        if (validationResult.siteId) {
          navigate(`/s/${validationResult.siteId}/`, { replace: true })
        } else {
          navigate('/', { replace: true })
        }
      } catch (error) {
        openToast({
          title: getErrorMessage(error, t('clients.errors.generic')),
          severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
        })
        navigate(ROUTES.login.path)
      } finally {
        setIsLoading(false)
      }
    }

    handleLoginAs()
  }, [searchParams, navigate, openToast, setCurrentSite, clearCurrentSite, t])

  if (isLoading) {
    return (
      <Box sx={styles.root}>
        <Loading />
      </Box>
    )
  }

  return null
}

export default LoginAs
